/**
 * Sprachnachrichten aufnehmen — und warum das Format der wunde Punkt war.
 *
 * **Der Server kann nicht umwandeln.** Kein ffmpeg auf dem Paket; was
 * hereinkommt, wird abgelegt und genau so wieder ausgeliefert. Damit
 * entscheidet der aufnehmende Browser, wer es später abspielen kann:
 *
 * - **MP4/AAC** spielt jeder — Safari, Chrome, Firefox, Windows. Safari
 *   nimmt ausschließlich das auf.
 * - **WebM/Opus** nimmt Chrome auf, wenn es MP4 nicht kann. **Safari spielt
 *   es nicht ab.** Das ist dasselbe Loch wie bei den Videos.
 * - **Ogg/Opus** nimmt Firefox auf; Safari spielt es ebenfalls nicht.
 *
 * Deshalb wird **MP4 zuerst erfragt** und erst danach der Rest.
 *
 * **Und wo trotzdem WebM oder Ogg herauskommt, rechnet der Browser um**
 * (`ueberallAbspielbar()`, `tonwandeln.ts`). Der Server kann es nicht, aber
 * er muss auch nicht: eine Sprachnachricht ist klein genug, um sie dort zu
 * wandeln, wo sie entsteht. Ein Video wäre es nicht — dort bleibt das Loch.
 *
 * Hier stand vorher, die Oberfläche sage es dem Aufnehmenden eben. Das war
 * ehrlich und trotzdem zu wenig: getroffen hätte es den Mitlesenden an einem
 * Android-Gerät, dessen Antwort beim Reisenden stumm bleibt — also den, der
 * am wenigsten dagegen tun kann.
 */

import { nachWav } from './tonwandeln'

/** Die Formate in der Reihenfolge, in der wir sie haben wollen. */
const FORMATE = [
  'audio/mp4',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
]

export interface Aufnahme {
  blob: Blob
  dauerMs: number
  /** Ob das Format auf jedem Gerät läuft — sonst steht ein Hinweis dabei. */
  ueberall: boolean
}

/** Ob dieser Browser überhaupt aufnehmen kann. */
export function kannAufnehmen(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    navigator.mediaDevices !== undefined &&
    typeof navigator.mediaDevices.getUserMedia === 'function'
  )
}

/** Das beste Format, das dieser Browser aufnimmt — oder `null`. */
function bestesFormat(): string | null {
  for (const format of FORMATE) {
    if (MediaRecorder.isTypeSupported(format)) return format
  }
  return null
}

/**
 * Eine laufende Aufnahme.
 *
 * `stoppen()` gibt die fertige Aufnahme zurück, `abbrechen()` wirft sie weg —
 * und **beide geben das Mikrofon frei**. Ein Aufnahmepunkt, der nach dem
 * Abbrechen weiterleuchtet, ist das, was Vertrauen kostet.
 */
export interface LaufendeAufnahme {
  stoppen: () => Promise<Aufnahme>
  abbrechen: () => void
}

export async function starteAufnahme(): Promise<LaufendeAufnahme> {
  const spur = await navigator.mediaDevices.getUserMedia({ audio: true })
  const format = bestesFormat()
  const recorder = new MediaRecorder(spur, format === null ? undefined : { mimeType: format })
  const stuecke: Blob[] = []
  const beginn = Date.now()

  recorder.ondataavailable = (ereignis) => {
    if (ereignis.data.size > 0) stuecke.push(ereignis.data)
  }
  recorder.start()

  const freigeben = () => spur.getTracks().forEach((t) => t.stop())

  return {
    stoppen: () =>
      new Promise<Aufnahme>((fertig) => {
        recorder.onstop = () => {
          freigeben()
          const typ = recorder.mimeType || format || 'audio/webm'
          fertig({
            blob: new Blob(stuecke, { type: typ }),
            dauerMs: Date.now() - beginn,
            ueberall: typ.startsWith('audio/mp4'),
          })
        }
        recorder.stop()
      }),
    abbrechen: () => {
      recorder.onstop = freigeben
      if (recorder.state !== 'inactive') recorder.stop()
      else freigeben()
    },
  }
}

/**
 * Aus einer Aufnahme eine machen, die überall läuft.
 *
 * Wo schon MP4 herauskam, passiert **nichts**: nicht dekodieren, nicht neu
 * rechnen, nicht neu verpacken. Das ist der Normalfall, und er kostet
 * dadurch weder Zeit noch Qualität noch Bytes.
 *
 * **Scheitert das Umwandeln, geht die Aufnahme trotzdem raus** — mit dem
 * alten Hinweis. Eine gesprochene Nachricht wegzuwerfen, weil eine
 * Umrechnung nicht ging, wäre die schlechtere Anwendung.
 */
export async function ueberallAbspielbar(roh: Aufnahme): Promise<Aufnahme> {
  if (roh.ueberall) return roh

  try {
    const blob = await nachWav(roh.blob)
    return { blob, dauerMs: roh.dauerMs, ueberall: true }
  } catch {
    return roh
  }
}

/** Aus Millisekunden „0:07" — für die Blase und den laufenden Zähler. */
export function dauer(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/**
 * Die Decke für eine Aufnahme.
 *
 * **Der Client deckelt die Länge, der Server die Bytes** — die Dauer ließe
 * sich dort ohne ffmpeg nicht nachprüfen, und eine Zahl, die man glauben
 * muss, ist keine Regel. Zwei Minuten sind eine Sprachnachricht; wer mehr zu
 * sagen hat, schreibt oder nimmt zweimal auf.
 */
export const HOECHSTENS_MS = 120_000
